import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(desc: string, lineSpacing: number, bScale: number) {
  if (!desc) return null;
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const style = { fontSize: `${0.875 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="text-slate-600 whitespace-pre-line" style={style}>{desc}</p>;
  }
  return (
    <ul className="list-disc pl-4 space-y-1 text-slate-600" style={style}>
      {lines.map((line, i) => <li key={i}>{line.replace(/^[-•\s]+/, '')}</li>)}
    </ul>
  );
}

export function ProfessionalTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="bg-white text-slate-800 h-full"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="border-b-4 border-slate-800 pb-6 mb-6" style={{ textAlign: s.headerAlignment }}>
        <h1 className="font-bold text-slate-900 tracking-tight" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${2.25 * hScale}rem` }}>
          {personal.fullName || 'Your Name'}
        </h1>
        {s.photo && <img src={s.photo} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mt-3 border-2 border-slate-800" />}
        <p className="text-slate-600 mt-1 font-medium" style={{ fontSize: `${1 * bScale}rem` }}>{personal.email}</p>
        <div className="flex flex-wrap gap-3 mt-3 text-sm text-slate-500 justify-center">
          {personal.phone && <span>{personal.phone}</span>}
          {personal.phone && personal.location && <span className="text-slate-300">|</span>}
          {personal.location && <span>{personal.location}</span>}
          {(personal.phone || personal.location) && personal.linkedin && <span className="text-slate-300">|</span>}
          {personal.linkedin && <span>{personal.linkedin}</span>}
          {personal.website && <span className="text-slate-300">|</span>}
          {personal.website && <span>{personal.website}</span>}
        </div>
      </header>

      <div className="flex gap-8">
        <div className="flex-[7]" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
          {experience.length > 0 && (
            <section>
              <h2 className="font-bold text-slate-900 mb-4 pb-2 border-b-2 border-slate-800 uppercase tracking-wide" style={{ fontSize: `${1.125 * hScale}rem` }}>Work Experience</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
                {experience.map((exp) => (
                  <div key={exp.id}>
                    <div className="flex justify-between items-baseline">
                      <h3 className="font-bold text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{exp.title}</h3>
                      <span className="text-slate-500 font-medium" style={{ fontSize: `${0.875 * bScale}rem` }}>
                        {formatDate(exp.startDate, s.dateFormat)} — {exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                      </span>
                    </div>
                    <p className="text-slate-700 font-semibold mt-0.5" style={{ fontSize: `${0.875 * bScale}rem` }}>
                      {exp.company}{exp.location && `, ${exp.location}`}
                    </p>
                    {renderDescription(exp.description, s.spacing.lineSpacing, bScale)}
                  </div>
                ))}
              </div>
            </section>
          )}

          {education.length > 0 && (
            <section>
              <h2 className="font-bold text-slate-900 mb-4 pb-2 border-b-2 border-slate-800 uppercase tracking-wide" style={{ fontSize: `${1.125 * hScale}rem` }}>Education</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
                {education.map((edu) => (
                  <div key={edu.id}>
                    <div className="flex justify-between items-baseline">
                      <h3 className="font-bold text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{edu.school}</h3>
                      <span className="text-slate-500" style={{ fontSize: `${0.875 * bScale}rem` }}>
                        {formatDate(edu.startDate, s.dateFormat)} — {formatDate(edu.endDate, s.dateFormat)}
                      </span>
                    </div>
                    <p className="text-slate-700 font-medium" style={{ fontSize: `${0.875 * bScale}rem` }}>{edu.degree}{edu.field && `, ${edu.field}`}</p>
                    {edu.description && <p className="text-slate-500 mt-1" style={{ fontSize: `${0.875 * bScale}rem` }}>{edu.description}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {projects.length > 0 && (
            <section>
              <h2 className="font-bold text-slate-900 mb-4 pb-2 border-b-2 border-slate-800 uppercase tracking-wide" style={{ fontSize: `${1.125 * hScale}rem` }}>Projects</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
                {projects.map((proj) => (
                  <div key={proj.id}>
                    <div className="flex justify-between items-baseline">
                      <h3 className="font-bold text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{proj.name}</h3>
                      {proj.url && <span className="text-slate-500" style={{ fontSize: `${0.875 * bScale}rem` }}>{proj.url}</span>}
                    </div>
                    <p className="text-slate-600 mt-1" style={{ fontSize: `${0.875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{proj.description}</p>
                    {proj.technologies.length > 0 && <p className="text-slate-500 mt-1" style={{ fontSize: `${0.875 * bScale}rem` }}><span className="font-semibold">Technologies:</span> {proj.technologies.join(', ')}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="flex-[3]" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
          {skills.length > 0 && (
            <section>
              <h2 className="font-bold text-slate-900 mb-3 pb-2 border-b-2 border-slate-800 uppercase tracking-wide" style={{ fontSize: `${1.125 * hScale}rem` }}>Skills</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.titleGap }}>
                {skills.map((skill) => (
                  <div key={skill.id} className="border-b border-slate-200 pb-1 font-semibold text-slate-800" style={{ fontSize: `${0.875 * bScale}rem` }}>
                    {skill.name}
                  </div>
                ))}
              </div>
            </section>
          )}

          {certifications.length > 0 && (
            <section>
              <h2 className="font-bold text-slate-900 mb-3 pb-2 border-b-2 border-slate-800 uppercase tracking-wide" style={{ fontSize: `${1.125 * hScale}rem` }}>Certifications</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
                {certifications.map((cert) => (
                  <div key={cert.id}>
                    <p className="font-semibold text-slate-800" style={{ fontSize: `${0.875 * bScale}rem` }}>{cert.name}</p>
                    <p className="text-slate-500" style={{ fontSize: `${0.75 * bScale}rem` }}>{cert.issuer}{cert.date && ` • ${cert.date}`}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
